import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Scale, Stethoscope, Building2, ShieldCheck, FlaskConical } from "lucide-react";
import { PetPromo } from "./VetCuraPet";
import { SearchForm } from "../components/SearchForm";
import { EmergencyButton } from "../components/EmergencyButton";
import { PriceTypeBadge, VerificationBadge } from "../components/Badges";
import { api } from "../lib/api";

const Coverage = () => {
  const [n, setN] = useState(null);
  useEffect(() => { api.get("/seo/config").then((r) => setN(r.data.counts)).catch(() => {}); }, []);
  return <p className="mt-4 text-xs text-[#52706D] flex items-center gap-1.5 min-h-[1rem]" data-testid="home-coverage"><FlaskConical className="h-3.5 w-3.5" />{n ? `${n.clinics} klinikker i oversigten · ${n.price_clinics} med verificerede priser · vi udvider løbende` : ""}</p>;
};

const CITY_LINKS = [["koebenhavn", "København"], ["aarhus", "Aarhus"], ["odense", "Odense"], ["aalborg", "Aalborg"], ["esbjerg", "Esbjerg"], ["roskilde", "Roskilde"], ["kolding", "Kolding"], ["vejle", "Vejle"]];

const Discover = () => (
  <section className="container-vp py-16 grid gap-10 lg:grid-cols-3" data-testid="home-discover">
    <div>
      <h2 className="text-xl font-bold text-[#153C3A]">Find en dyrlæge nær dig</h2>
      <p className="text-sm text-[#52706D] mt-2">Søg på by, postnummer eller region, og se adresse, hjemmeside og prisstatus for dyreklinikker og dyrehospitaler.</p>
      <ul className="mt-3 grid grid-cols-2 gap-1 text-sm">{CITY_LINKS.map(([s, n]) => <li key={s}><Link to={`/dyrlaege/${s}`} className="text-[#087F72] hover:underline" data-testid={`home-city-${s}`}>Dyrlæge {n}</Link></li>)}</ul>
    </div>
    <div>
      <h2 className="text-xl font-bold text-[#153C3A]">Behandlinger for hund og kat</h2>
      <p className="text-sm text-[#52706D] mt-2">Vaccination, kastration, sterilisation, tandrensning, konsultation og meget mere. Priserne afhænger ofte af dyrets art og vægt, så vælg altid hund eller kat.</p>
      <div className="flex gap-3 mt-3 text-sm"><Link to="/sammenlign?species=dog" className="text-[#087F72] hover:underline">Priser for hund</Link><Link to="/sammenlign?species=cat" className="text-[#087F72] hover:underline">Priser for kat</Link><Link to="/behandlinger" className="text-[#087F72] hover:underline">Alle behandlinger</Link></div>
    </div>
    <div>
      <h2 className="text-xl font-bold text-[#153C3A]">Gennemsigtige priser</h2>
      <p className="text-sm text-[#52706D] mt-2">Priserne kommer fra klinikkernes egne prislister og vises med kilde og dato for seneste kontrol. Læs hvorfor priserne varierer, og hvordan du sammenligner dem fair.</p>
      <div className="flex flex-wrap gap-3 mt-3 text-sm"><Link to="/guide" className="text-[#087F72] hover:underline" data-testid="home-guide-link">Guide til dyrlægepriser</Link><Link to="/guide/saadan-verificerer-vetpris-priser" className="text-[#087F72] hover:underline">Sådan verificerer vi</Link></div>
    </div>
  </section>
);

const Hero = () => (
  <section className="relative overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,#E6F4EF_0%,#F7FAF9_60%)]" aria-hidden="true" />
    <div className="container-vp relative grid lg:grid-cols-12 gap-10 py-14 sm:py-20 items-center">
      <div className="lg:col-span-7">
        <p className="eyebrow rise">Uafhængig prisportal for dyrlæger</p>
        <h1 className="rise rise-1 mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-[#153C3A]" data-testid="home-hero-title">
          Sammenlign dyrlægepriser og find <span className="text-[#087F72]">dyreklinikker i Danmark</span>
        </h1>
        <p className="rise rise-2 mt-5 text-base md:text-lg text-[#52706D] max-w-xl">
          Find dyrlæger, dyreklinikker og dyrehospitaler, og sammenlign priser på almindelige behandlinger for hund og kat – med tydelig markering af faste priser, fra-priser og estimater.
        </p>
        <div className="rise rise-3 mt-8"><SearchForm /></div>
        <EmergencyButton className="mt-4" testid="home-emergency-button" />
        <Coverage />
      </div>
      <div className="lg:col-span-5 relative hidden md:block">
        <div className="rounded-[2rem] overflow-hidden aspect-[4/5] border border-[#D2E5E1] shadow-xl">
          <img src="/images/hero.jpg" alt="Dyrlæge undersøger en hund" className="w-full h-full object-cover" width="800" height="1000" fetchpriority="high" />
        </div>
        <div className="absolute -left-8 bottom-10 card-vp p-4 w-64 shadow-xl rise rise-3" aria-hidden="true">
          <span className="text-xs font-semibold text-[#52706D]">Tydelig mærkning af hver pris</span>
          <div className="flex flex-wrap gap-1.5 mt-2"><PriceTypeBadge type="FIXED" testid="hero-sample-type-fixed" /><PriceTypeBadge type="FROM" testid="hero-sample-type" /><PriceTypeBadge type="ESTIMATE" testid="hero-sample-type-estimate" /></div>
          <div className="flex gap-1.5 mt-2"><VerificationBadge status="CLINIC_CONFIRMED" testid="hero-sample-verification" /></div>
        </div>
      </div>
    </div>
  </section>
);

const Popular = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/public/services").then((r) => setItems(r.data.filter((s) => s.is_popular))).catch(() => {});
  }, []);
  return (
    <section className="container-vp py-16 sm:py-20" data-testid="popular-treatments">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div><p className="eyebrow">Populære behandlinger</p><h2 className="text-2xl sm:text-3xl font-bold text-[#153C3A] mt-2">Det sammenligner andre dyreejere</h2></div>
        <Link to="/behandlinger" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-[#087F72] hover:underline" data-testid="all-treatments-link">Alle behandlinger <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((s, i) => (
          <Link key={s.id} to={`/behandlinger/${s.slug}`} data-testid={`popular-treatment-${s.slug}`}
            className={`group card-vp p-6 hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] ${i === 0 ? "lg:col-span-2 bg-[#153C3A] border-[#153C3A]" : ""}`}>
            <Stethoscope className={`h-6 w-6 ${i === 0 ? "text-[#F0A94C]" : "text-[#087F72]"}`} aria-hidden="true" />
            <h3 className={`font-heading text-lg font-bold mt-4 ${i === 0 ? "text-white" : "text-[#153C3A]"}`}>{s.name_da}</h3>
            <p className={`text-sm mt-1 line-clamp-2 ${i === 0 ? "text-[#B9D6D0]" : "text-[#52706D]"}`}>{s.description_da}</p>
            <span className={`inline-flex items-center gap-1 text-sm font-semibold mt-4 ${i === 0 ? "text-[#F0A94C]" : "text-[#087F72]"}`}>Se priser <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
          </Link>
        ))}
      </div>
    </section>
  );
};

const STEPS = [
  [Search, "Vælg dyr og behandling", "Vælg hund eller kat, den behandling du har brug for og dit postnummer."],
  [Scale, "Sammenlign priser", "Se priser fra klinikker i nærheden, sorteret efter pris eller afstand, med tydelige betingelser."],
  [ShieldCheck, "Vælg med overblik", "Se om prisen er fast, en fra-pris eller et estimat, og hvem der har verificeret den."],
];

const HowItWorks = () => (
  <section className="bg-white border-y border-[#D2E5E1]" data-testid="how-it-works">
    <div className="container-vp py-16 sm:py-20">
      <p className="eyebrow">Sådan fungerer VetPris</p>
      <h2 className="text-2xl sm:text-3xl font-bold text-[#153C3A] mt-2 max-w-xl">Tre trin til et bedre overblik over dyrlægepriser</h2>
      <ol className="grid gap-6 md:grid-cols-3 mt-10">
        {STEPS.map(([Icon, title, text], i) => (
          <li key={title} className="relative pl-0">
            <span className="font-heading text-6xl font-extrabold text-[#E6F4EF] absolute -top-4 right-2 select-none" aria-hidden="true">0{i + 1}</span>
            <div className="h-12 w-12 rounded-xl bg-[#E6F4EF] flex items-center justify-center"><Icon className="h-6 w-6 text-[#087F72]" aria-hidden="true" /></div>
            <h3 className="font-heading text-lg font-bold text-[#153C3A] mt-4">{title}</h3>
            <p className="text-sm text-[#52706D] mt-2 leading-relaxed">{text}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const PriceTypes = () => (
  <section className="container-vp py-16 sm:py-20 grid lg:grid-cols-2 gap-10 items-center">
    <div>
      <p className="eyebrow">Gennemskuelige priser</p>
      <h2 className="text-2xl sm:text-3xl font-bold text-[#153C3A] mt-2">Vi viser altid, hvilken slags pris du ser</h2>
      <p className="text-[#52706D] mt-4 leading-relaxed">Dyrlægepriser afhænger ofte af dyrets vægt, alder og helbred. Derfor markerer vi hver pris tydeligt og viser betingelser og kilde.</p>
    </div>
    <div className="grid gap-3">
      {[["FIXED", "Prisen gælder som angivet under de nævnte betingelser."], ["FROM", "Laveste pris – den endelige pris kan blive højere."], ["ESTIMATE", "Vejledende prisinterval. Kontakt klinikken for et tilbud."]].map(([t, d]) => (
        <div key={t} className="card-vp p-4 flex items-start gap-4"><PriceTypeBadge type={t} testid={`home-price-type-${t.toLowerCase()}`} /><p className="text-sm text-[#334155]">{d}</p></div>
      ))}
    </div>
  </section>
);

const ClinicCta = () => (
  <section className="container-vp" data-testid="clinic-info-section">
    <div className="rounded-[2rem] bg-[#153C3A] text-white grid md:grid-cols-5 overflow-hidden">
      <div className="md:col-span-3 p-8 sm:p-12">
        <Building2 className="h-8 w-8 text-[#F0A94C]" aria-hidden="true" />
        <h2 className="text-2xl sm:text-3xl font-bold mt-4">Er du dyreklinik?</h2>
        <p className="text-[#B9D6D0] mt-3 max-w-lg">Overtag jeres klinikprofil, hold priserne opdaterede og bekræft dem selv. Det er gratis i fase 1, og alle ejerskabsanmodninger godkendes manuelt af VetPris.</p>
        <div className="flex flex-wrap gap-3 mt-6">
          <Link to="/for-klinikker" className="btn-accent" data-testid="home-for-clinics-button">Læs mere for klinikker</Link>
          <Link to="/login?tab=register" className="inline-flex items-center h-11 px-5 rounded-full border border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors" data-testid="home-clinic-register-button">Opret klinikkonto</Link>
        </div>
      </div>
      <div className="md:col-span-2 min-h-[220px]"><img src="/images/clinic.jpg" alt="Hund på dyreklinik" className="w-full h-full object-cover" loading="lazy" width="600" height="400" /></div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Popular />
      <Discover />
      <section className="container-vp pb-16" data-testid="home-vetcurapet"><PetPromo testid="home-vetcurapet-promo" /></section>
      <HowItWorks />
      <PriceTypes />
      <ClinicCta />
    </div>
  );
}
